import { ALL_GRANTS, ORGANIZATION_ADMIN_NAME, VENUE_MANAGER_DEFAULTS, VENUE_MANAGER_NAME } from '@lib/constants/permissions'
import type { AuthStaffAccessDto } from '@lib/dto/auth.dto'
import type { ApiTokenScope } from '@lib/enums/api-token-scope.enum'
import { apiTokenScopesFor, grantsMeet, type PermissionNeed } from '@lib/helpers/permissions'
import { isInTenantSupportMode, isInVenueSupportMode } from '@/auth/roles'
import type { User } from '@/schemas/auth.schema'

/**
 * What the session may do, by its company role (docs/custom-roles.spec.md). The
 * API decides; these only keep the app from offering what it would refuse. Ask
 * `can` for an area, and `reachesCompany` for whether the role spans the company,
 * rather than asking which kind of account someone has.
 */

/** An organization admin's, and CSE's while supporting a company: everything, everywhere in it. */
const ORGANIZATION_ADMIN_ACCESS: AuthStaffAccessDto = { roleName: ORGANIZATION_ADMIN_NAME, reach: 'company', grants: ALL_GRANTS }

/**
 * What a venue admin could do before the API said: the built-in Venue manager's
 * defaults, which every venue admin had. Only for a session from an API older
 * than this build, for the minutes a deploy takes (`authStaffAccessDtoSchema`).
 */
const VENUE_MANAGER_ACCESS: AuthStaffAccessDto = { roleName: VENUE_MANAGER_NAME, reach: 'venues', grants: VENUE_MANAGER_DEFAULTS }

/** The session's staff role, or null for anyone who isn't a company's staff: a client, a vendor, CSE outside a company. */
export function staffAccessOf(user: User | null | undefined): AuthStaffAccessDto | null {
  if (user == null) return null
  if (isInTenantSupportMode(user)) return ORGANIZATION_ADMIN_ACCESS
  if (user.role === 'ORGANIZATION_ADMIN') return user.access ?? ORGANIZATION_ADMIN_ACCESS
  if (user.role === 'VENUE_ADMIN') return user.access ?? VENUE_MANAGER_ACCESS
  return null
}

/** True for a company's staff, whatever their role allows: organization admins, venue staff, and CSE supporting the company. */
export function isStaff(user: User | null | undefined): boolean {
  return staffAccessOf(user) !== null
}

/** Whether the session's role allows `need`: an area at View or Edit (Edit includes View), or deleting events. */
export function can(user: User | null | undefined, need: PermissionNeed): boolean {
  const access = staffAccessOf(user)
  return access !== null && grantsMeet(access.grants, need)
}

/** Staff whose role reaches the whole company: organization admins, CSE supporting it, and Company-wide roles. */
export function reachesCompany(user: User | null | undefined): boolean {
  return staffAccessOf(user)?.reach === 'company'
}

/**
 * Staff whose role works at the venues they are assigned to. Their selected
 * venue is where they are looking; their assignments are what they reach.
 */
export function worksAtAssignedVenues(user: User | null | undefined): boolean {
  return staffAccessOf(user)?.reach === 'venues'
}

/**
 * Staff working across the company right now: a Company-wide role that hasn't
 * entered a venue. This is the line between the company's business and a
 * venue's: someone who has entered a venue is working as its staff, so the
 * company's decisions (adding venues, who manages one) wait until they leave.
 * Both halves must be checked; the role alone still reaches the company while
 * the venue is entered, which is how those controls kept appearing inside one.
 */
export function atCompanyLevel(user: User | null | undefined): boolean {
  return reachesCompany(user) && !isInVenueSupportMode(user)
}

/**
 * Whether this session creates events: staff who can edit events, working at a
 * venue — on an Assigned-venues role, or having entered one. Across the whole
 * company there is no venue being booked, so the form isn't offered there.
 */
export function canCreateEvents(user: User | null | undefined): boolean {
  return can(user, 'events:edit') && !atCompanyLevel(user)
}

/**
 * Where "up" from a venue page goes, and what to call it.
 *
 * Anyone working inside a venue goes to that venue's dashboard, not the venues
 * list: staff on assigned venues have no such list, and someone who has entered
 * a venue is working as its staff, so their nav does not offer the company's
 * entries either — leaving is the picker's job, not a breadcrumb's. Only staff
 * working across the company go to Venues.
 *
 * Kept in one place so the next venue page cannot reintroduce the mismatch.
 */
export function venuesUpTarget(user: User | null | undefined): { to: string; label: string } {
  if (isInVenueSupportMode(user)) {
    return { to: `/venues/${user.supportVenue.venueId}`, label: 'Dashboard' }
  }
  // Assigned-venue staff with no venue on their session have no dashboard to
  // point at; their events are all they can reach.
  if (worksAtAssignedVenues(user)) return { to: '/events', label: 'Events' }
  return { to: '/venues/all', label: 'Venues' }
}

/**
 * Where a session lands when it has nowhere in particular to be: after signing
 * in, after entering a venue, and whenever a page's rule bounces someone home.
 *
 * Inside a venue that is the venue's own dashboard — the month, and what still
 * needs doing — rather than its events list. The list answers "which events are
 * there"; the question someone opens the app with is "what is on", and it is the
 * dashboard that answers it. Stated ahead of the account checks so a super admin
 * holding a venue overlay never lands on /tenants.
 *
 * One function, because the same answer is needed in three places and a session
 * that lands somewhere different depending on how it got there is a session that
 * feels broken.
 */
export function homePathFor(user: User | null | undefined): string {
  if (isInVenueSupportMode(user)) return `/venues/${user.supportVenue.venueId}`
  if (isInTenantSupportMode(user)) return '/events'
  if (user?.role === 'SUPER_ADMIN') return '/tenants'
  // Every vendor lands on its own Events list: a planner's work, and in time
  // any vendor's, is the events it is on (docs/planner-vendors.md).
  if (user?.role === 'VENDOR') return '/vendor/events'
  // Staff whose role doesn't see events start where their work is: the
  // company's venues, or their own profile until they have a venue.
  if (isStaff(user) && !can(user, 'events:view')) return reachesCompany(user) ? '/venues/all' : '/profile'
  return '/events'
}

/**
 * Staff who set up the company's integrations, working where that is done:
 * across the company, or at their own assigned venues. Someone who has entered a
 * venue is working as its staff, and connects GoHighLevel once they're back out,
 * as an organization admin always has.
 */
export function setsUpIntegrations(user: User | null | undefined): boolean {
  return can(user, 'integrations:edit') && (worksAtAssignedVenues(user) || atCompanyLevel(user))
}

/**
 * The scopes this person may put on an API token, as the API decides them: from
 * their kind of account and their role. None means they can't hold one.
 */
export function tokenScopesOf(user: User | null | undefined): readonly ApiTokenScope[] {
  if (user == null) return []
  return apiTokenScopesFor({ role: user.role, grants: staffAccessOf(user)?.grants ?? null })
}

/**
 * What to call the session's account where a person reads it: their company
 * role's name for staff, as the company named it, and otherwise the kind of
 * account.
 */
export function accountLabelOf(user: User | null | undefined): string {
  if (user == null) return ''
  if (isInTenantSupportMode(user)) return 'Super admin · support'
  const access = staffAccessOf(user)
  if (access !== null) return access.roleName
  if (user.role === 'SUPER_ADMIN') return 'Super admin'
  return user.role === 'VENDOR' ? 'Vendor' : 'Client'
}

/**
 * Who a page lets in, as the API's `@Access` says who a route does
 * (api/src/auth/access.decorator.ts). Each kind of account is named on its own,
 * and staff by what their role must allow.
 */
export interface AccessRule {
  /** Staff whose role allows every one of these (`ANY_STAFF`: any staff). Organization admins always pass. */
  staff?: PermissionNeed | readonly PermissionNeed[]
  /** With `staff`: the role must also reach the whole company. */
  companyWide?: true
  /** Organization admins only, whatever a role allows: roles and billing are theirs. */
  organizationAdmin?: true
  client?: true
  vendor?: true
  /** CSE's own pages, outside any company. CSE supporting a company works there as its staff instead. */
  platform?: true
}

/** `staff` for a page any member of the company's staff may open, whatever their role allows. */
export const ANY_STAFF: readonly PermissionNeed[] = []

/** Whether `rule` lets this session in. */
export function admits(user: User | null | undefined, rule: AccessRule): boolean {
  if (user == null) return false
  const staffPage = rule.staff !== undefined || rule.organizationAdmin === true
  switch (user.role) {
    case 'SUPER_ADMIN':
      // Supporting a company, CSE works as its organization admin and keeps
      // none of the platform's own pages; it leaves by the support banner.
      return isInTenantSupportMode(user) ? staffPage || rule.client === true : rule.platform === true
    case 'ORGANIZATION_ADMIN':
      return staffPage
    case 'VENUE_ADMIN': {
      if (rule.staff === undefined) return false
      const needs: readonly PermissionNeed[] = typeof rule.staff === 'string' ? [rule.staff] : rule.staff
      return needs.every((need) => can(user, need)) && (rule.companyWide !== true || reachesCompany(user))
    }
    case 'CLIENT':
      return rule.client === true
    case 'VENDOR':
      return rule.vendor === true
  }
}
